import type { Mark } from '@xox/shared'
import { useEffect, useRef } from 'react'
import { useCountdown } from '../hooks/useCountdown'
import { feedback } from '../lib/feedback'

interface Props {
  deadlineAt: number | null
  turnMs: number
  turn?: Mark
  mine: boolean
  name: string
  /** Overrides the "X's turn" wording, e.g. "Race". */
  label?: string
  /** Bar color when not urgent; defaults to the mark's color. */
  tone?: 'x' | 'o' | 'neutral'
  /** The room's server clock (defaults to the XOX room's). */
  serverNow?: () => number
}

/** Slim countdown under the seats; ticks audibly for the last 5s of your own turn. */
export function TurnBar({
  deadlineAt,
  turnMs,
  turn = 'X',
  mine,
  name,
  label,
  tone,
  serverNow,
}: Props) {
  const remaining = useCountdown(deadlineAt, serverNow)
  const seconds = remaining === null ? null : Math.ceil(remaining / 1000)
  const lastTick = useRef<number | null>(null)

  useEffect(() => {
    if (!mine || seconds === null || seconds > 5 || seconds <= 0) return
    if (lastTick.current === seconds) return
    lastTick.current = seconds
    feedback('tick')
  }, [seconds, mine])

  if (remaining === null || seconds === null) return null

  const frac = Math.min(1, Math.max(0, remaining / turnMs))
  const urgent = remaining < 5000
  const base = tone ?? (turn === 'X' ? 'x' : 'o')
  const fill = urgent
    ? 'bg-amber-500'
    : base === 'x'
      ? 'bg-x'
      : base === 'o'
        ? 'bg-o'
        : 'bg-sky-500'
  const text = label ?? `${mine ? 'Your' : `${name}'s`} turn`

  return (
    <div className="flex w-full max-w-[520px] items-center gap-3">
      <div
        className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={Math.round(turnMs / 1000)}
        aria-valuenow={seconds}
        aria-label={`${text}, ${seconds} seconds left`}
      >
        <div
          className={`h-full rounded-full ${fill}`}
          style={{ width: `${frac * 100}%`, transition: 'width 200ms linear' }}
        />
      </div>
      <span
        className={`w-9 text-right text-sm font-bold tabular-nums ${urgent ? 'text-amber-600' : 'text-slate-500'}`}
      >
        {seconds}s
      </span>
    </div>
  )
}
